import Utilities from '@/Utility/Utilities';
import ResourceManager from '@/Management/ResourceManager';

export default class TypeConverter {

    public static isValidDotNetType(value: string, dotNetTypeName: string): boolean {
        if (!dotNetTypeName) {
            return true;
        }

        if (TypeConverter.typeIsArray(dotNetTypeName)) {
            return TypeConverter.isArrayValidDotNetType(value, dotNetTypeName, ',');
        } else {
            return TypeConverter.isValueValidDotNetType(value, dotNetTypeName);
        }
    }

    public static parseIntoDotNetType(value: string, dotNetTypeName: string) {
        if (!TypeConverter.isValidDotNetType(value, dotNetTypeName)) {
            throw 'Cannot convert "' + value + '" into "' + dotNetTypeName + '"';
        }
    }

    /**
     * Prüft für jeden Wert eines Arrays, ob dieser in den dotNetType geparst werden kann
     */
    public static isArrayValidDotNetType(value, dotNetTypeName, separator) {
        let result = true;
        let values = Utilities.isNullOrUndefined(value) ? [] : value.split(separator);

        for (let i = 0; i < values.length; i++) {
            if (!TypeConverter.isValueValidDotNetType(values[i], dotNetTypeName)) {
                result = false;
            }
        }

        return result;
    }

    /**
     * Prüft einen Wert, ob dieser in den dotNetType geparst werden kann
     */
    public static isValueValidDotNetType(value: any, dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(value) && !TypeConverter.typeIsNullable(dotNetTypeName)) {
            return false;
        }

        if (TypeConverter.typeIsBool(dotNetTypeName) && !TypeConverter.valueIsBool(value)) {
            return false;
        }

        if (TypeConverter.typeIsChar(dotNetTypeName) && !TypeConverter.valueIsChar(value)) {
            return false;
        }

        if (TypeConverter.typeIsNumber(dotNetTypeName) && !TypeConverter.valueIsNumber(value, dotNetTypeName)) {
            return false;
        }

        return true;
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein Array-Typ ist
     */
    public static typeIsArray(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return dotNetTypeName.indexOf('[]') > -1;
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein numerischer Array-Typ ist
     */
    public static typeIsNumericArray(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        if (!TypeConverter.typeIsArray(dotNetTypeName))
            return false;

        const arrayType = TypeConverter.getArrayType(dotNetTypeName);

        return TypeConverter.typeIsNumber(arrayType);
    }

    /**
     * Gibt den Typ des Arrays zurück
     * @param dotNetTypeName
     */
    public static getArrayType(dotNetTypeName: string): string {
        return dotNetTypeName.substr(0, dotNetTypeName.indexOf('[]'));
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein Nullable-Typ ist
     */
    public static typeIsNullable(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return dotNetTypeName.indexOf('System.Nullable') === 0 ||
            TypeConverter.typeIsString(dotNetTypeName);
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein Char ist
     */
    public static typeIsChar(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return dotNetTypeName === 'System.Char';
    }

    /**
     * Prüft, ob ein übergebener string in ein char geparst werden kann
     */
    public static valueIsChar(value) {
        return !Utilities.isNullOrUndefined(value) && value.length === 1;
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein Char ist
     */
    public static typeIsString(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return dotNetTypeName === 'System.String';
    }

    public static typeIsEncryptedString(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return dotNetTypeName.indexOf('TypEncryptedString') > -1;
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein DateTime ist
     */
    public static typeIsDateTime(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return dotNetTypeName === 'System.DateTime'
            || dotNetTypeName.startsWith('System.Nullable`1[[System.DateTime');
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein Boolean ist
     */
    public static typeIsBool(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return dotNetTypeName === 'System.Boolean';
    }

    /**
     * Prüft, ob ein übergebener string in ein bool geparst werden kann
     */
    public static valueIsBool(value: any): boolean {
        if (!Utilities.isNullOrUndefined(value)) {
            value = value.toLowerCase();

            return !Utilities.isNullOrUndefined(value) && (value === 'true' || value === 'false');
        }

        return false;
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein Byte ist
     */
    public static typeIsByte(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return dotNetTypeName === 'System.Byte' 
            || dotNetTypeName.startsWith('System.Nullable`1[[System.Byte');
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein Single ist
     */
    public static typeIsSingle(dotNetTypeName: string): boolean {
        return dotNetTypeName === 'System.Single'
            || dotNetTypeName.startsWith('System.Nullable`1[[System.Single');
    }

    /**
    * Prüft, ob ein übergebener dotNetTypeName ein Decimal ist
    */
    public static typeIsDecimal(dotNetTypeName: string): boolean {
        return dotNetTypeName === 'System.Decimal'
            || dotNetTypeName.startsWith('System.Nullable`1[[System.Decimal');
    }

    /**
    * Prüft, ob ein übergebener dotNetTypeName ein Double ist
    */
    public static typeIsDouble(dotNetTypeName: string): boolean {
        return dotNetTypeName === 'System.Double'
            || dotNetTypeName.startsWith('System.Nullable`1[[System.Double');
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein Int ist
     */
    public static typeIsInt(dotNetTypeName: string): boolean {
        return dotNetTypeName.startsWith('System.Int')
            || dotNetTypeName.startsWith('System.Nullable`1[[System.Int');
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein numerischer Typ ist
     */
    public static typeIsNumber(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return TypeConverter.typeIsFloatingNumber(dotNetTypeName) || TypeConverter.typeIsNonFloatingNumber(dotNetTypeName);
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein numerischer gleitkomma-Typ ist
     */
    public static typeIsFloatingNumber(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return TypeConverter.typeIsSingle(dotNetTypeName)
            || TypeConverter.typeIsDouble(dotNetTypeName)
            || TypeConverter.typeIsDecimal(dotNetTypeName);
    }

    /**
     * Prüft, ob ein übergebener dotNetTypeName ein numerischer nicht-gleitkomma-Typ ist
     */
    public static typeIsNonFloatingNumber(dotNetTypeName: string): boolean {
        if (Utilities.isNullOrUndefined(dotNetTypeName))
            return false;

        return TypeConverter.typeIsInt(dotNetTypeName) || TypeConverter.typeIsByte(dotNetTypeName);
    }

    /**
     * Prüft, ob ein übergebener string in einen numerischen Typ geparst werden kann
     */
    public static valueIsNumber(value: any, dotNetTypeName: string): boolean {
        if (TypeConverter.typeIsFloatingNumber(dotNetTypeName)) {
            return TypeConverter.valueIsFloatingNumber(value);
        }

        if (TypeConverter.typeIsNonFloatingNumber(dotNetTypeName)) {
            return TypeConverter.valueIsNonFloatingNumber(value);
        }

        return false;
    }

    /**
     * Prüft, ob ein übergebener string in einen numerischen Gleitkomma-Typen geparst werden kann
     */
    public static valueIsFloatingNumber(value: any): boolean {
        return !Utilities.isNullOrUndefined(value) && !isNaN(value);
    }

    /**
     * Prüft, ob ein übergebener string in einen numerischen Nicht-Gleitkomma-Typen geparst werden kann
     */
    public static valueIsNonFloatingNumber(value: any): boolean {
        return !Utilities.isNullOrUndefined(value) && !isNaN(value) && !TypeConverter.valueHasDecimalSeparator(value);
    }

    /**
     * Prüft, ob ein übergebener string einen Decimal separator enthält
     */
    public static valueHasDecimalSeparator(value: string): boolean {
        let separator = TypeConverter.getDecimalSeparator();

        if (!Utilities.isNullOrUndefined(value)) {
            return value.indexOf(separator) > -1;
        }

        return false;
    }

    /**
     * Liefert den Decimal separator der aktuell ausgewählten Sprache
     */
    public static getDecimalSeparator(): string {
        let language = ResourceManager.getInstance().getLanguage();
        let currencyPattern = language.currencyPattern;

        let replaced = currencyPattern.replace(/#/g, '');
        let decimalSeparator = replaced[replaced.length - 1];

        return decimalSeparator;
    }
}